'use client';

import { hasRolePlayer, useAuth } from '@svenskaspelab/nova-core/nextjs/auth';
import { useEffect, useRef } from 'react';

import { useSessionTime } from '../../store/zustand.js';
import type { ShowDialogFn } from '../../types.js';
import { HOUR_IN_MS, MINUTES_IN_MS } from './constants.js';
import useNextHour from './hooks/useNextHour.js';
import { getClosestHourMinutes, getNextHourMinutes, getTimeToNextHourMinutes } from './lib/time-utils.js';

export default function RealityChecks({ showDialog }: { showDialog: ShowDialogFn }) {
  const { user, logout } = useAuth();
  const isPlayer = hasRolePlayer(user);
  const sessionStartTime = useSessionTime((state) => state.sessionStartTime);
  const sessionStartTimeRef = useRef(sessionStartTime);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const intervalRef = useRef<ReturnType<typeof setInterval>>(undefined);
  const onNextHour = useNextHour(showDialog, logout);
  const onNextHourRef = useRef(onNextHour);

  useEffect(() => {
    sessionStartTimeRef.current = sessionStartTime;
  }, [sessionStartTime]);

  useEffect(() => {
    onNextHourRef.current = onNextHour;
  });

  useEffect(() => {
    const startRealityChecksInterval = () => {
      clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        const minutes = getClosestHourMinutes(sessionStartTimeRef.current);
        onNextHourRef.current(minutes);
      }, HOUR_IN_MS);
    };

    if (isPlayer && user) {
      const { sessionTimeMs } = user as Required<typeof user>;
      const nextHourInMinutes = getNextHourMinutes(sessionStartTime);
      const timeToNextHourMinutes = getTimeToNextHourMinutes(sessionTimeMs, sessionStartTime);

      timeoutRef.current = setTimeout(() => {
        onNextHourRef.current(nextHourInMinutes);
        startRealityChecksInterval();
      }, timeToNextHourMinutes * MINUTES_IN_MS);
    }
    return () => {
      clearTimeout(timeoutRef.current);
      clearInterval(intervalRef.current);
    };
  }, [isPlayer, user, sessionStartTime]);

  return null;
}
